import { ConsentDisplay } from '../consent-display';
import { FormBuilder } from '@act/storybook';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from 'redux/store-hooks';
import { selectedMemberSelectors } from 'redux/slices/selectedMemberSlice';
import {
  consentAppSelectors,
  consentTermSuccess,
} from 'redux/slices/consent.slice';
import { useEffect } from 'react';
import { consentRequests } from 'redux/requests/consents.requests';
import { useApiCall } from 'redux/utils/api-hook';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConsentFormSchema } from '@act/shared/data-transfer-objects';
import { BaseDialog } from '../../../stories/dialogs/shared/components/BaseDialog';
import { useTranslation } from 'react-i18next';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { useToast } from 'react-components/app/ReusableToast';
import { z } from 'zod';

export type ConsentFormType = z.infer<typeof ConsentFormSchema>;

const ConsentTermForm = ({ term, open, handleClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const userId = selectedMemberSelectors.useSelectSelectedMemberId();
  const termIdentityId = consentAppSelectors.useSelectTermIdentityId();
  const [termResponse, _isSubmittingTerm, updateTerm] = useApiCall(
    consentRequests.updateConsent
  );

  const { control, formState, handleSubmit, reset, watch } =
    useForm<ConsentFormType>({
      mode: 'onChange',
      defaultValues: {
        consented: term.consented ?? undefined,
        termId: term.id ?? undefined,
        consentIdentityId: termIdentityId ?? undefined,
      },
      resolver: zodResolver(ConsentFormSchema),
    });

  useEffect(() => {
    reset({
      consented: term.consented ?? undefined,
      termId: term.id ?? undefined,
      consentIdentityId: termIdentityId ?? undefined,
    });
  }, [term, termIdentityId, reset]);

  const consentedValue = watch('consented');

  const submitConsent = async (_data: ConsentFormType) => {
    updateTerm({
      params: { userId },
      body: {
        termId: term.id,
        consented: consentedValue,
        consentIdentityId: term.identityRequired ? termIdentityId : null,
      },
    });
  };

  useEffect(() => {
    if (termResponse) {
      dispatch(consentTermSuccess(termResponse));
      handleClose();
      toast('Consent updated successfully', 'success');
    }
  }, [termResponse, dispatch, handleClose]);

  const options = [
    {
      key: 'accept',
      label: term.acceptText,
      value: true,
    },
  ];
  if (term.rejectText) {
    options.push({
      key: 'reject',
      label: term.rejectText,
      value: false,
    });
  }

  const formConfig: FormConfig<ConsentFormType> = [
    {
      type: 'input',
      name: 'consented',
      label: term.name,
      input: 'radioGroup',
      props: {
        options: options,
        required: true,
        row: true,
      },
    },
  ];

  return (
    <BaseDialog
      open={open}
      title={term.name}
      setOpen={handleClose}
      actions={[
        {
          text: t('consentForm.confirm'),
          buttonColor: 'primary',
          isPrimary: true,
          callback: handleSubmit(submitConsent),
          disabled:
            !!formState.isSubmitting ||
            !formState.isDirty ||
            consentedValue === null,
        },
      ]}
    >
      <>
        <ConsentDisplay term={term} />
        <FormBuilder
          config={formConfig}
          control={control}
          formState={formState}
        />
      </>
    </BaseDialog>
  );
};
export default ConsentTermForm;
